import { EventAggregator } from 'aurelia-event-aggregator';
import {
	autoinject,
	bindable,
	computedFrom,
	containerless,
	observable,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { StateHistory, Store } from 'aurelia-store';
import debounce from 'lodash-es/debounce';
import throttle from 'lodash-es/throttle';
import PerfectScrollbar from 'perfect-scrollbar';
import { Container } from 'postcss';

import { NavigationAPI } from '~/api/navigation-api';
import { AppEvents } from '~/app';
import { AppState, initialState } from '~/app-store';
import { BaseViewModel } from '~/base-view-model';
import { NavigationActions } from '~/components/navigation/navigation-actions';
import { NavigationState } from '~/components/navigation/navigation-state';
import {
	NavigationBookmark,
	NavigationEntity,
	NavigationHistory,
	NavigationMenuItem,
} from '~/components/navigation/navigation-types';
import {
	computeNavURL,
	filterStatefulURLQueryParams,
	generateBookmarkFromRouter,
} from '~/components/navigation/navigation-utils';
import { Notification } from '~/components/notification/notification';
import {
	ANIMATION_DURATION,
	CommonURLParam,
	DEBOUNCE_DURATION,
	ShortcutKey,
	THROTTLE_DURATION,
} from '~/constants';
import { EntityType } from '~/models/entities/entity-type';
import { SettingsState } from '~/settings-state/settings-state';
import { FocusTrap } from '~/utils/focus-trap';
import { Key } from '~/utils/key';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { setItem } from '~/utils/local-storage';
import { Logger } from '~/utils/logger';

enum NavigationInitStatus {
	DEFAULT = 'DEFAULT', // aka empty
	INITIALIZING = 'INITIALIZING', // API request still running
	SUCCESS = 'SUCCESS', // structure is in store
	FAILED = 'FAILED', // nav structure request fail; do not repeat
}

interface NavigationGroup {
	type: 'bookmark' | 'history';
	class: string;
	menuList: NavigationBookmark[];
}

interface EntityTypeItem {
	id: string;
	name: string;
	type: EntityType;
}

@autoinject
@containerless
export class Navigation extends BaseViewModel {
	/**
	 * Initial navigation state
	 */
	public static readonly initialState = initialState.navigation;

	/**
	 * Used to append the current routeParams in the navigation menu links
	 */
	@bindable public routeParams: string = '?';

	/**
	 * Used to append the current statefulRouteParams in the navigation menu links
	 */
	@bindable public statefulRouteParams: string = '?';

	/**
	 * Quick filter query -- used for highlights
	 */
	@observable({ changeHandler: 'filterQueryChanged' })
	private filterQuery: string = '';

	/**
	 * Active menu category
	 * - main-menu (globe)
	 * - favorites (star)
	 * - history
	 */
	@bindable private selectedMenuCategory: string = 'main-menu';

	/**
	 * Prefix for the routes
	 * '/#/' by default; but this will change when `config.options.root` is defined in `App.ts`
	 * Used in the `nav-*.html` pages
	 */
	private readonly routePathRoot = '/#/';

	/**
	 * Element class names
	 */
	private readonly classNames = {
		appCollapsed: 'a-app--collapsed',
		category: 'a-app__menu__category',
		navigationInitializing: 'a-app--navigation-initializing',
		verticalMenuBookmarks: 'a-app__menu__vertical__bookmarks',
		verticalMenuHistory: 'a-app__menu__vertical__history',
		viewMenuVisible: 'a-app--view-menu-visible',
	};

	/**
	 * Element CSS selectors
	 */
	private readonly selectors = {
		app: '.a-app',
		category: '.a-app__menu__category',
		categoryItemBtn: '.a-app__menu__category > button',
		collapseToggleBtn: 'button.a-app__menu__collapse-toggle',
		entityList: '.a-app__top__nav',
		entityListItem: '.a-app__top__nav__item',
		filter: 'input.a-app__menu__filter__input',
		logoutBtn: 'li.a-app__view__menu__item--logout > a',
		verticalMenu: '.a-app__menu__vertical',
		verticalMenuBookmarks: '.a-app__menu__vertical__bookmarks',
		verticalMenuHistory: '.a-app__menu__vertical__history',
		verticalMenuMainMenu: '.a-app__menu__vertical__main-menu',
		viewMenu: '.a-app__view',
		viewMenuBtn: '.a-app__top__view-menu > button',
		viewMenuItemBtn: '.a-app__view__menu__item > a',
		viewMenuViews: '.a-app__view__menu',
	};

	/**
	 * Naive measure in preventing duplicate `InitializeNavigationViewStructures` action calls
	 */
	private eventHandlersInitialized: boolean = false;

	private readonly refSearchInput: HTMLInputElement;

	private readonly refNavigationMenu: HTMLElement;

	private readonly navLabel = this.settingsState.state.navLabel;

	private psMainNav: PerfectScrollbar;

	private psNavGroup: { [type: string]: PerfectScrollbar } = {
		bookmark: undefined,
		history: undefined,
	};

	private readonly refMainMenuList: HTMLElement;

	private readonly refVerticalMenu: HTMLElement;

	private readonly refNavGroup: { [type: string]: HTMLElement } = {
		bookmark: undefined,
		history: undefined,
	};

	constructor(
		protected readonly store: Store<StateHistory<AppState>>,
		protected readonly eventAggregator: EventAggregator,
		protected readonly router: Router,
		protected readonly i18n: I18N,
		private readonly navigationAPI: NavigationAPI,
		private readonly navState: NavigationState,
		private readonly settingsState: SettingsState,
	) {
		super();
	}

	protected attached(): void {
		// Subscribe to events
		this.subscribe();

		KeyboardShortcut.subscribe(
			this,
			ShortcutKey.JumpToNavFilter,
			this.onJumpToNavFilterShortcut,
		);
	}

	private detached(): void {
		if (this.psMainNav) {
			this.psMainNav.destroy();
			this.psMainNav = undefined;
		}
		for (const type in this.psNavGroup) {
			if (!this.psNavGroup.hasOwnProperty(type)) {
				continue;
			}
			const ps = this.psNavGroup[type];
			if (!ps) {
				continue;
			}
			ps.destroy();
			this.psNavGroup[type] = undefined;
		}
	}

	/**
	 * Subscribe to events
	 */
	private subscribe() {
		// State changes
		this.subscribeToStateChanges({
			callback: (_) => {
				if (!this.showNavigation) {
					return;
				}

				if (!this.eventHandlersInitialized) {
					this.resetInterface();
					this.initEventHandlers();
					this.setMenuPerfectScrollbar();
				}
			},
			requireLoggedIn: true,
		});
		this.processURLParams();
		this.subscribeToEvent('router:navigation:complete', () => {
			this.processURLParams();
			this.logToNavigationHistory();
		});
	}

	private readonly onJumpToNavFilterShortcut = (): void => {
		if (this.refSearchInput) {
			this.refSearchInput.focus();
		}
	};

	private onSearchKeydown(e: KeyboardEvent): boolean {
		if (
			e.key !== Key.ARROW_DOWN &&
			e.key !== Key.ENTER &&
			e.key !== Key.ESCAPE
		) {
			return true;
		}
		const currentEl = e.target as HTMLInputElement;
		switch (e.key) {
			case Key.ARROW_DOWN:
				this.navItemArrowDown(currentEl);
				return false;

			case Key.ESCAPE:
				// NOTE: Jake: 2019-11-19 - WEBUI-2738
				// Make sure search resets when you press ESCAPE.
				// Sometimes this just isn't working by default as we expect. (ie. type="search" should do this)
				this.filterQuery = '';
				currentEl.value = '';
				return false;

			case Key.ENTER: {
				// NOTE: Jake: 2019-09-09
				// Intent is that the first item in the filtered results is:
				// - Focused
				// - ... then selected
				const elements = FocusTrap.getTabbableNodes(
					this.refNavigationMenu,
				);
				const nextEl = Navigation.getNextElement(elements, currentEl);
				if (nextEl !== undefined) {
					// NOTE: Jake: 2019-09-09
					// Chrome acts weird when you focus an element without
					// applying tabindex=0. ie. No outline, won't show hyperlink
					// in status bar.
					if (!nextEl.hasAttribute('tabindex')) {
						nextEl.setAttribute('tabindex', '0');
					}
					nextEl.focus();
					nextEl.click();
				}
				return false;
			}
		}
		return true;
	}

	private onSearchInput(e: InputEvent): void {
		if (!(e.target instanceof HTMLInputElement)) {
			throw new Error('Expected <input> element.');
		}
		this.filterQuery = e.target.value;
	}

	private onNavItemKeydown(e: KeyboardEvent): boolean {
		if (
			e.key !== Key.ARROW_DOWN &&
			e.key !== Key.ARROW_UP &&
			e.key !== Key.ENTER &&
			e.key !== Key.ESCAPE
		) {
			return true;
		}
		if (!this.refNavigationMenu) {
			if (DEVELOPMENT) {
				throw new Error('Missing "refNavigationMenu".');
			}
			return true;
		}
		const currentEl = e.target as HTMLElement;
		switch (e.key) {
			case Key.ARROW_UP:
				this.navItemArrowUp(currentEl);
				return false;

			case Key.ARROW_DOWN:
				this.navItemArrowDown(currentEl);
				return false;

			case Key.ESCAPE:
				this.refSearchInput.focus();
				return false;

			case Key.ENTER:
				currentEl.click();
				return false;
		}
		return true;
	}

	private navItemArrowUp(currentEl: HTMLElement): void {
		const elements = FocusTrap.getTabbableNodes(this.refNavigationMenu);
		for (let i = elements.length - 1; i >= 0; i--) {
			const el = elements[i];
			if (el === currentEl) {
				if (i - 1 >= 0) {
					const prevEl = elements[i - 1];
					// NOTE: Jake: 2019-09-09
					// We ignore PerfectScrollbar which has a tabindex
					// on a <div>
					if (prevEl.tagName !== 'DIV') {
						// NOTE: Jake: 2019-09-09
						// Chrome acts weird when you focus an element without
						// applying tabindex=0. ie. No outline, won't show hyperlink
						// in status bar.
						if (!prevEl.hasAttribute('tabindex')) {
							prevEl.setAttribute('tabindex', '0');
						}
						prevEl.focus();
					}
				}
				break;
			}
		}
	}

	private static getNextElement(
		elements: HTMLElement[],
		currentElement: HTMLElement,
	): HTMLElement | undefined {
		for (let i = 0; i < elements.length; i++) {
			const el = elements[i];
			if (el === currentElement) {
				if (i + 1 < elements.length) {
					const nextEl = elements[i + 1];
					// NOTE: Jake: 2019-09-09
					// We ignore PerfectScrollbar which has a tabindex
					// on a <div>
					if (nextEl.tagName !== 'DIV') {
						return nextEl;
					}
				}
				break;
			}
		}
		return undefined;
	}

	private navItemArrowDown(currentEl: HTMLElement): void {
		const elements = FocusTrap.getTabbableNodes(this.refNavigationMenu);
		const nextEl = Navigation.getNextElement(elements, currentEl);
		if (nextEl !== undefined) {
			// NOTE: Jake: 2019-09-09
			// Chrome acts weird when you focus an element without
			// applying tabindex=0. ie. No outline, won't show hyperlink
			// in status bar.
			if (!nextEl.hasAttribute('tabindex')) {
				nextEl.setAttribute('tabindex', '0');
			}
			nextEl.focus();
		}
	}

	/**
	 * When router finishes navigating to route
	 */
	private processURLParams() {
		const params = new URLSearchParams(window.location.hash.split('?')[1]);
		this.statefulRouteParams =
			'?' + filterStatefulURLQueryParams(params).toString();
		this.routeParams = '?' + params.toString();

		// Log current route
		let currentRoute: string | string[] = '';
		if (
			this.router.currentInstruction &&
			this.router.currentInstruction.config &&
			this.router.currentInstruction.config.route
		) {
			currentRoute = this.router.currentInstruction.config.route;
		}
		Logger.log(
			'Route navigation completed:',
			currentRoute,
			'   URL params:',
			params.toString(),
		);

		// Expand parent of selected menu item
		$(`${this.selectors.verticalMenuMainMenu} a.selected`)
			.closest('li.expandable')
			.addClass('expanded')
			.find('ul')
			.slideDown(ANIMATION_DURATION);

		// Save to history
		this.logToNavigationHistory();
	}

	/**
	 * Set the menu's PerfectScrollbar if it doesn't have already
	 */
	private setMenuPerfectScrollbar(): void {
		// NOTE: Jake: 2019-11-19 - WEBUI-2738
		// We use jQuery to wait because we currenlty <compose> in navigation.html
		// which is slow / means elements don't immediately mount.
		$(() => {
			if (
				this.appState.present.isLoggedIn &&
				this.showNavigation &&
				this.menuElem
			) {
				if (!this.psMainNav) {
					this.psMainNav = new PerfectScrollbar(
						this.refMainMenuList,
						{
							suppressScrollX: true,
						},
					);
				} else {
					this.psMainNav.update();
				}
				// Add history / bookmark scrollbar
				for (const type in this.psNavGroup) {
					if (!this.psNavGroup.hasOwnProperty(type)) {
						continue;
					}
					const ps = this.psNavGroup[type];
					if (!ps) {
						const ref = this.refNavGroup[type];
						if (ref === undefined) {
							throw new Error('Missing ref for type: ' + type);
						}
						this.psNavGroup[type] = new PerfectScrollbar(ref, {
							suppressScrollX: true,
						});
					} else {
						ps.update();
					}
				}
			}
		});
	}

	/**
	 * Initialize event handlers
	 */
	private initEventHandlers() {
		this.eventHandlersInitialized = true;

		$(() => {
			Logger.log('Initializing navigation event handlers');
			this.removeEventHandlers();

			// Main Menu: Expand global view menu
			if (this.viewMenuBtnElem) {
				this.viewMenuBtnElem.addEventListener('click', (event) =>
					this.viewMenuButtonClicked(event),
				);
			} else {
				Logger.warning(
					'Unable to register click event listener for global menu hamburger',
				);
			}

			$(this.selectors.categoryItemBtn).on('click', (event) =>
				this.menuCategoryItemClicked(event),
			);

			// View links
			$(this.selectors.viewMenuViews).on(
				'click',
				this.selectors.viewMenuItemBtn,
				() => this.viewItemClicked(),
			);

			// Vertical: expand expandable menu items on click
			// Register event handler when document is ready
			$(this.selectors.verticalMenu).on(
				'click',
				'li.expandable > button',
				throttle(
					(event) => this.parentMenuItemClicked(event),
					THROTTLE_DURATION,
				),
			);

			// Vertical: expand the expanded (if 'expanded' thru .html template)
			$(
				`${this.selectors.verticalMenu} li.expandable.expanded ul`,
			).slideDown(ANIMATION_DURATION);

			// Collapse main menu
			if (this.collapseToggleBtnElem) {
				this.collapseToggleBtnElem.addEventListener(
					'click',
					throttle((event: MouseEvent) => {
						event.preventDefault();
						event.stopPropagation();
						this.toggleNavigationCollapsed(
							!this.isNavigationCollapsed,
						);
					}, THROTTLE_DURATION),
				);
			}

			// Logout button event
			if (this.logoutBtnElem) {
				this.logoutBtnElem.addEventListener(
					'click',
					debounce((event: MouseEvent) => {
						event.preventDefault();
						event.stopPropagation();
						this.publishEvent(AppEvents.Logout);
					}, DEBOUNCE_DURATION),
				);
			}
		});
	}

	/**
	 * Main Menu: Expand global view menu
	 */
	private viewMenuButtonClicked(event: MouseEvent) {
		event.preventDefault();
		event.stopPropagation();

		this.toggleNavigationCollapsed(false);
		this.appElem.classList.toggle(this.classNames.viewMenuVisible)
			? $(this.selectors.viewMenu).fadeIn(ANIMATION_DURATION)
			: $(this.selectors.viewMenu).fadeOut(ANIMATION_DURATION);

		return false;
	}

	/**
	 * Hide view menu when clicking a view item
	 */
	private viewItemClicked() {
		this.toggleNavigationCollapsed(false);
		this.appElem.classList.remove(this.classNames.viewMenuVisible);
		$(this.selectors.viewMenu).fadeOut(ANIMATION_DURATION);
		return true;
	}

	/**
	 * menuCategoryItemClicked change between main menu, bookmarks and history
	 */
	private menuCategoryItemClicked(event): boolean {
		event.preventDefault();
		event.stopPropagation();

		const selectedBtn = event.currentTarget as HTMLButtonElement;
		if (!selectedBtn) {
			if (DEVELOPMENT) {
				throw new Error('Missing element');
			}
			return false;
		}
		const dataset = selectedBtn.dataset;
		this.selectedMenuCategory =
			dataset && dataset.category ? dataset.category : 'main-menu';

		return false;
	}

	/**
	 * When `selectedMenuCategory` changed, clear the quick filter text
	 */
	private selectedMenuCategoryChanged(category, prevCategory) {
		if (prevCategory && category !== prevCategory) {
			this.clearFilterInput();
		}
	}

	/**
	 * computeBookmarkOrHistoryItemTitle replaces "converters/route-title" component
	 */
	private computeBookmarkOrHistoryItemTitle(item: NavigationHistory): string {
		const translated = this.i18n.tr(`route-title.${item.formName}`);
		return translated.startsWith('route-title')
			? item.formName
			: translated;
	}

	/**
	 * Triggered by changing `filterQuery` property
	 * This is because we need to keep track of the old filter
	 */
	private filterQueryChanged(query: string, previousQuery: string): void {
		// NOTE: Jake: 2020-01-20 - WEBUI-2931
		// We wait 1 frame for the "menuItemList" to be updated, otherwise the
		// PerfectScrollbar updates based on the DOM too soon.
		window.requestAnimationFrame(() => {
			if (query) {
				// Find parents with at least one matching child
				// Then expand that parent if it isn't already
				$(
					`${this.selectors.verticalMenuMainMenu} > li > ul > li[data-route*="${query}" i]`,
				)
					.closest('li.expandable')
					.css('display', 'block')
					.addClass('expanded')
					.find('ul')
					.slideDown(ANIMATION_DURATION);
			} else if (!query && previousQuery) {
				// Slide up and hide expandables if query is about to be empty
				$(`${this.selectors.verticalMenuMainMenu} li.expandable`)
					.removeClass('expanded')
					.find('ul')
					.slideUp(ANIMATION_DURATION);
			}

			// NOTE: Jake: 2019-11-19 - WEBUI-2738
			// Must trigger update so that if we're scrolled down to the bottom
			// of the navigation list and *then* we search, it'll bring the scroll
			// offset back to the top rather than in a zone where nothing is showing.
			if (this.psMainNav) {
				this.psMainNav.update();
			}
			for (const type in this.psNavGroup) {
				if (!this.psNavGroup.hasOwnProperty(type)) {
					continue;
				}
				const ps = this.psNavGroup[type];
				if (!ps) {
					continue;
				}
				ps.update();
			}
		});
	}

	@computedFrom(
		'navState.structure',
		'router.currentInstruction.queryParams',
		'filterQuery',
	)
	get menuItemList(): NavigationMenuItem[] {
		if (!this.currentView) {
			return [];
		}
		const view = this.navState.structure[this.currentView];
		if (view === undefined) {
			return [];
		}
		const searchEntityId = this.searchEntityId;
		const currentEntity = view.structure.entityList.find(
			(entity) => entity.id === searchEntityId,
		);
		if (!currentEntity) {
			return [];
		}
		if (!currentEntity.menuItemList) {
			return [];
		}
		return Navigation.filterMenuItemList(
			currentEntity.menuItemList,
			this.filterQuery,
		);
	}

	private static filterMenuItemList(
		items: NavigationMenuItem[],
		filterQuery: string,
	): NavigationMenuItem[] {
		const hasFilter = filterQuery && filterQuery.trim().length > 0;
		const matcher = new RegExp(filterQuery.replace(' ', '\\s'), 'i');
		const result: NavigationMenuItem[] = [];
		for (const item of items) {
			if (item.hideFromMenu) {
				continue;
			}
			if (hasFilter) {
				let hasMatch: boolean = matcher.test(item.trName);
				// Keep item in the list if we match on the children
				if (!hasMatch && item.childMenuItems) {
					for (const childItem of item.childMenuItems) {
						hasMatch = hasMatch || matcher.test(childItem.trName);
					}
				}
				if (!hasMatch) {
					continue;
				}
			}
			const childMenuItems = [];
			for (const childItem of item.childMenuItems) {
				if (childItem.hideFromMenu) {
					continue;
				}
				childMenuItems.push(childItem);
			}
			result.push({
				...item,
				childMenuItems,
			});
		}
		return result;
	}

	private filterBookmarkOrHistoryList(
		items: NavigationHistory[],
		filterQuery: string,
	): NavigationHistory[] {
		if (!filterQuery || !filterQuery.trim()) {
			// If no filter, return list as-is
			return items;
		}
		const matcher = new RegExp(filterQuery.replace(' ', '\\s'), 'i');
		const result: NavigationHistory[] = [];
		for (const item of items) {
			const trName = this.computeBookmarkOrHistoryItemTitle(item);
			const hasMatch: boolean = matcher.test(trName);
			if (!hasMatch) {
				continue;
			}
			result.push(item);
		}
		return result;
	}

	/**
	 * Expand/collapse parent menu items
	 * Also, they aren't really links so this prevents navigation to an empty link
	 */
	private parentMenuItemClicked(event) {
		event.preventDefault();
		event.stopPropagation();

		const $li: JQuery<HTMLElement> = $(event.currentTarget.parentElement);
		$li.toggleClass('expanded')
			.find('ul')
			.each((_, ulElem) => {
				$li.hasClass('expanded')
					? $(ulElem).slideDown(ANIMATION_DURATION)
					: $(ulElem).slideUp(ANIMATION_DURATION);
			});

		return false;
	}

	/**
	 * Remove all event handlers on this page
	 */
	private removeEventHandlers() {
		// Everything that gets registered with an event via `initEventHandlers`
		$(this.selectors.app).off();
		$(this.selectors.categoryItemBtn).off();
		$(this.selectors.collapseToggleBtn).off();
		$(this.selectors.filter).off();
		$(this.selectors.filter).off();
		$(this.selectors.logoutBtn).off();
		$(this.selectors.verticalMenu).off();
		$(this.selectors.viewMenuBtn).off();
		$(this.selectors.viewMenuViews).off();
	}

	/**
	 * Reset navigation interface
	 */
	private resetInterface() {
		Logger.log('Resetting navigation interface');

		$(() => {
			// Hide global menu
			if (this.appElem) {
				this.appElem.classList.remove(this.classNames.viewMenuVisible);
			}

			// Reset the filter input
			this.clearFilterInput();

			// Hide the global menu
			if (this.viewMenuElem) {
				this.viewMenuElem.style.display = 'none';
			}
		});

		this.eventHandlersInitialized = false;
		this.removeEventHandlers();
	}

	/**
	 * Clear filter input value and `filterQuery`
	 */
	private clearFilterInput() {
		if (this.refSearchInput) {
			this.refSearchInput.value = '';
		} else {
			Logger.warning('Unable to reset the value for filter input field');
		}
		this.filterQuery = '';
	}

	/**
	 * Save to navigation history
	 */
	private logToNavigationHistory() {
		if (!this.appState.present.isLoggedIn) {
			Logger.warning(
				'Unable to log to navigation history because you are not logged in',
			);
			return;
		}
		const historyItem = generateBookmarkFromRouter(this.router);
		Logger.log(`Logging to navigation history: ${historyItem.formName}`);
		// Save to history
		this.dispatch(NavigationActions.LogNavigationHistory, historyItem);
	}

	/**
	 * Gets the first form route from a given view and entityType
	 * If entityType is not provided, get the first entityType in that structure
	 * Inspect the state "navigation.structure" to better understand this
	 * This is used in `navigation-top.html` and `navigation-view-menu.html`
	 */
	private getFirstFormInViewAndEntityType(
		view: string,
		entityType?: string,
	): string {
		const menuItem = this.getFirstNavigationMenuItemInViewAndEntityType(
			view,
			entityType,
		);
		if (menuItem) {
			return menuItem.formName;
		} else {
			return '';
		}
	}

	/**
	 * Gets the first menu item from a given view and entityType.
	 * If entityType is not provided, get the first entityType in that structure.
	 * Inspect the state "navigation.structure" to better understand this.
	 */
	private getFirstNavigationMenuItemInViewAndEntityType(
		view: string,
		entityType?: string,
	): NavigationMenuItem | undefined {
		if (!view) {
			Logger.debug(
				`Unable to get the first form route from view: "${view}"`,
			);
			return undefined;
		}

		const viewStructure = this.navState.structure[view];
		if (!viewStructure) {
			Logger.debug(
				`Unable to retrieve the navigation structure for view: "${view}"`,
			);
			return undefined;
		}

		if (viewStructure.structure.entityList.length === 0) {
			// Ignore if no menu items loaded yet
			return undefined;
		}

		let navEntityStructure: NavigationEntity | undefined;
		if (!entityType) {
			navEntityStructure = viewStructure.structure.entityList[0];
		} else {
			for (const otherNavEntity of viewStructure.structure.entityList) {
				if (otherNavEntity.entityType === entityType) {
					navEntityStructure = otherNavEntity;
					break;
				}
			}
		}
		if (!navEntityStructure) {
			Logger.warning(
				`Unable to retrieve the navigation structure for entity type: "${entityType}"`,
			);
			return undefined;
		}

		// If the first menu item's formName is null, it could most likely be a parent menu item
		const menuItem = navEntityStructure.menuItemList[0];
		if (!menuItem.formName) {
			return menuItem.childMenuItems[0];
		}
		return menuItem;
	}

	private computeBookmarkURL(
		item: NavigationBookmark,
		routeParams: string,
	): string {
		let urlPart = '';
		if (item.formName) {
			urlPart = item.formName;
		}
		const params = new URLSearchParams(routeParams.split('?')[1]);
		// NOTE: Jake: 2019-05-02
		// If we don't remove the CUST_PARAM_NAME from the URL, a user won't be able
		// to navigate from a form without a customisation if their on a form w/ customisationName configured page
		params.delete(CommonURLParam.CUSTOMISATION);
		params.set(CommonURLParam.NAV_ID, item.searchEntityType);
		urlPart += `?${params.toString()}`;
		if (item.routeParams && item.routeParams.customisationName) {
			urlPart += `&${CommonURLParam.CUSTOMISATION}=${item.routeParams.customisationName}`;
		}
		return urlPart;
	}

	/**
	 * computeNavURL is utilized by templates to build the URLs to visit various classic forms.
	 */
	private computeNavURL(
		item: NavigationMenuItem,
		routeParams: string,
	): string {
		return computeNavURL(item, routeParams);
	}

	/**
	 * computeNavViewURL is utilized by templates to build the URLs to change what navigation view you're on.
	 */
	private computeNavViewURL(view: string, routeParams: string): string {
		const formName = this.getFirstFormInViewAndEntityType(view);
		let urlPart = formName;
		// NOTE: Jake: 2019-06-05 - WEBUI-2057
		// We want to clear all entity params, so we don't do anything
		// with the 'routeParams' parameter anymore (or for the time being)
		const params = new URLSearchParams();
		params.set(CommonURLParam.NAV_VIEW, view);
		urlPart += `?${params.toString()}`;
		return urlPart;
	}

	/**
	 * computeNavEntityURL is used on MEMBER, EMPLOYER tabs on the top of the navigation
	 */
	private computeNavEntityURL(entity: EntityTypeItem): string {
		const menuItem = this.getFirstNavigationMenuItemInViewAndEntityType(
			this.currentView,
			entity.type,
		);

		const params = new URLSearchParams(
			this.statefulRouteParams.split('?')[1],
		);
		params.set(CommonURLParam.NAV_ID, entity.id);

		return computeNavURL(menuItem, params);
	}

	/**
	 * isBookmarkCurrentRoute is typically utilized to highlight bookmarks and history items
	 */
	private isBookmarkCurrentRoute(
		item: NavigationBookmark,
		currentRoute: string,
		routeParams: string,
	): boolean {
		if (item.formName && item.formName === currentRoute) {
			// Handle raw data from navigation JSON
			const params = new URLSearchParams(routeParams.split('?')[1]);
			const customisationName: string | null = params.get(
				CommonURLParam.CUSTOMISATION,
			);
			if (
				((!item.routeParams || !item.routeParams.customisationName) &&
					!customisationName) ||
				(item.routeParams &&
					item.routeParams.customisationName === customisationName)
			) {
				return true;
			}
		}
		return false;
	}

	/**
	 * isCurrentRoute is typically utilized to highlight the currently active navigation item.
	 * Returns true if the currentRoute and customisationName Query parameter in routeParams matches
	 * the items's formName and customisationName properties.
	 */
	private isCurrentRoute(
		item: NavigationMenuItem,
		currentRoute: string,
		routeParams: string,
	): boolean {
		if (item.formName && item.formName === currentRoute) {
			// Handle raw data from navigation JSON
			const params = new URLSearchParams(routeParams.split('?')[1]);
			const customisationName: string | null = params.get(
				CommonURLParam.CUSTOMISATION,
			);
			if (
				(!item.customisationName && !customisationName) ||
				item.customisationName === customisationName
			) {
				return true;
			}
		}
		return false;
	}

	@computedFrom(
		'appState.present.navigation.bookmarks',
		'appState.present.navigation.history',
		'currentView',
		'filterQuery',
	)
	get navigationGroups(): NavigationGroup[] {
		if (!this.appState) {
			return [];
		}
		const navigation = this.appState.present.navigation;
		if (!navigation) {
			return [];
		}
		return [
			{
				type: 'bookmark',
				class: this.classNames.verticalMenuBookmarks,
				menuList: this.filterBookmarkOrHistoryList(
					navigation.bookmarks.filter(
						(bookmark) => bookmark.currentView === this.currentView,
					),
					this.filterQuery,
				),
			},
			{
				type: 'history',
				class: this.classNames.verticalMenuHistory,
				menuList: this.filterBookmarkOrHistoryList(
					navigation.history.filter(
						(history) => history.currentView === this.currentView,
					),
					this.filterQuery,
				),
			},
		];
	}

	/**
	 * Toggle navigation collapsed
	 */
	private toggleNavigationCollapsed(collapsed: boolean) {
		this.dispatch(NavigationActions.CollapseNavigation, collapsed);
	}

	/**
	 * Is navigation collapsed according to the state?
	 */
	@computedFrom('appState.present.navigation.collapsed')
	get isNavigationCollapsed(): boolean {
		const navigation = this.appState.present.navigation;
		if (!navigation) {
			return Navigation.initialState.collapsed;
		}
		return navigation.collapsed;
	}

	/**
	 * Navigation namespaces to be shown in the global menu
	 */
	@computedFrom('navState.viewNameList', 'navState.viewNameList.length')
	get navigationViews(): string[] {
		return this.navState.viewNameList;
	}

	/**
	 * Navigation entity list types for the selected navigation namespace
	 */
	@computedFrom('router.currentInstruction.queryParams')
	private get entityListTypes(): EntityTypeItem[] {
		if (!this.navState.structure) {
			return [];
		}
		const structure = this.navState.structure[this.currentView];
		if (structure === undefined) {
			return [];
		}
		const viewStructure = structure.structure;
		const res: EntityTypeItem[] = [];
		for (const entity of viewStructure.entityList) {
			res.push({
				id: entity.id,
				name: entity.displayName,
				type: entity.entityType,
			});
		}
		return res;
	}

	/**
	 * To be used by templates via `show.bind`
	 */
	@computedFrom('router.currentInstruction.queryParams')
	get searchEntityId(): string {
		const id =
			this.router.currentInstruction.queryParams[CommonURLParam.NAV_ID];
		const view =
			this.router.currentInstruction.queryParams[CommonURLParam.NAV_VIEW];
		const entity = this.navState.getEntityFromID(id, view);
		if (entity === undefined) {
			return '';
		}
		return entity.id;
	}

	/**
	 * The app element
	 */
	get appElem(): HTMLElement {
		return (
			this.getElementBySelector(this.selectors.app) ||
			Logger.warning('Unable to retrieve app element') ||
			null
		);
	}

	/**
	 * The global menu button
	 */
	get viewMenuBtnElem(): HTMLButtonElement {
		return (
			this.getElementBySelector<HTMLButtonElement>(
				this.selectors.viewMenuBtn,
			) ||
			Logger.warning('Unable to retrieve global menu button') ||
			null
		);
	}

	/**
	 * The menu element
	 */
	get viewMenuElem(): HTMLElement {
		return (
			this.getElementBySelector(this.selectors.viewMenu) ||
			Logger.warning('Unable to retrieve global menu element') ||
			null
		);
	}

	/**
	 * The menu element
	 */
	get menuElem(): HTMLElement {
		if (!this.refVerticalMenu) {
			Logger.warning('Unable to retrieve menu element');
			return null;
		}
		return this.refVerticalMenu;
	}

	/**
	 * To be used by templates via `show.bind`
	 */
	@computedFrom('router.currentInstruction.queryParams["nav-view"]')
	get currentView(): string {
		const currentView =
			this.router.currentInstruction.queryParams[CommonURLParam.NAV_VIEW];
		if (!currentView) {
			return '';
		}
		return currentView;
	}

	/**
	 * The nav collapse toggle button element
	 */
	get collapseToggleBtnElem(): HTMLButtonElement {
		return (
			this.getElementBySelector<HTMLButtonElement>(
				this.selectors.collapseToggleBtn,
			) ||
			Logger.warning(
				'Unable to retrieve nav collapse toggle button element',
			) ||
			null
		);
	}

	/**
	 * The logout button element
	 */
	get logoutBtnElem(): HTMLButtonElement {
		return (
			this.getElementBySelector<HTMLButtonElement>(
				this.selectors.logoutBtn,
			) ||
			Logger.warning('Unable to retrieve logout button element') ||
			null
		);
	}

	/**
	 * Helper method for getting a single element
	 *
	 * @param {string}					selector	Query selector
	 * @param {HTMLElement | Document}	source		Source/parent element
	 *
	 * @deprecated You should never need to use this. Just querySelector() without this wrapper.
	 * This was moved from base-view-model.ts to here as this is the only remaining place we
	 * use this deprecated functionality.
	 */
	private getElementBySelector = <T extends HTMLElement>(
		selector: string,
		source: HTMLElement | Document = document,
	): T => source.querySelector(selector);
}
